import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileText, Loader2, ArrowLeft, Mail } from 'lucide-react';
import { toast } from 'sonner';

export const ForgotPasswordPage = ({ onSwitchToLogin }) => {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err) {
      // Still show success to not reveal if email exists
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-sm border border-[#E5E5E5] p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-[#2C4C3B]/10 flex items-center justify-center mx-auto mb-4">
              <Mail className="w-6 h-6 text-[#2C4C3B]" />
            </div>
            <h2 className="text-lg font-medium text-[#1C1C1C] mb-2">Check your email</h2>
            <p className="text-sm text-[#595959] mb-2">
              If an account exists for {email}, we've sent a password reset link.
            </p>
            <p className="text-xs text-[#595959] mb-6">
              If you don't see the email in 2–3 minutes, check Spam/Promotions.
            </p>
            <Button
              variant="outline"
              onClick={onSwitchToLogin}
              className="rounded-xl"
              data-testid="back-to-login-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Sign In
            </Button>
            <p className="text-xs text-[#595959] mt-6">
              Trouble logging in? Email{' '}
              <a href="mailto:simplyslps@gmail.com" className="text-[#2C4C3B] hover:underline">
                simplyslps@gmail.com
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-sm border border-[#E5E5E5] p-8">
          {/* Logo */}
          <div className="flex items-center justify-center gap-2.5 mb-8">
            <div className="w-10 h-10 rounded-xl bg-[#2C4C3B] flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <h1 className="font-semibold text-xl text-[#1C1C1C]">Rubric Builder</h1>
          </div>

          <h2 className="text-lg font-medium text-center text-[#1C1C1C] mb-2">Forgot your password?</h2>
          <p className="text-sm text-[#595959] text-center mb-6">
            Enter your email and we'll send you a reset link
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@school.edu"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-email-input"
                disabled={loading}
              />
            </div>

            <Button
              type="submit"
              className="w-full rounded-xl"
              disabled={loading}
              data-testid="send-reset-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={onSwitchToLogin}
              className="text-sm text-muted-foreground hover:text-primary"
              data-testid="back-to-login-link"
            >
              <ArrowLeft className="w-4 h-4 inline mr-1" />
              Back to Sign In
            </button>
          </div>
          
          <p className="text-xs text-muted-foreground text-center mt-6">
            Trouble logging in? Email{' '}
            <a href="mailto:simplyslps@gmail.com" className="text-primary hover:underline">
              simplyslps@gmail.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};
